import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Logo, Logout } from "../assets/icons/Icons";
import { useUserData } from "../lib/contexts/UserContext";
import { useHospDocData } from "../lib/contexts/HospitalDoctorContext";
import { useTVConfiguration } from "../lib/contexts/TVConfigurationContext";
import { useFeatureFlags } from "../lib/contexts/FeatureFlagContext";
import { getProfilesByHospitalId, getProfileByHospitalAndProfileId } from "../lib/apis/profile";
import { getDoctorListByHospitalId } from "../lib/apis/doctor";
import { setAudioLanguageToLocalStorage } from "../lib/utils/speechUtils";
import { Doctor } from "../lib/utils/types";
import { toast } from "react-toastify";

type ProfileOption = {
  id: string;
  title: string;
  icon: "user" | "settings";
  colors: {
    defaultBg: string;
    activeBg?: string;
    border?: string;
    activeBorder?: string;
    icon: string;
    activeIcon?: string;
  };
  isDefault?: boolean;
};


const UserIcon = ({ color }: { color: string }) => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
    <path
      d="M16 16C18.7614 16 21 13.7614 21 11C21 8.23858 18.7614 6 16 6C13.2386 6 11 8.23858 11 11C11 13.7614 13.2386 16 16 16Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7 26C7 22.134 10.134 19 14 19H18C21.866 19 25 22.134 25 26"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SettingsIcon = ({ color }: { color: string }) => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
    <path
      d="M10.325 4.317a1.65 1.65 0 013.35 0l.116.996a1.651 1.651 0 002.458 1.304l.88-.51a1.65 1.65 0 012.25.6l1.65 2.86a1.65 1.65 0 01-.6 2.25l-.88.51a1.651 1.651 0 000 2.862l.88.51a1.65 1.65 0 01.6 2.25l-1.65 2.86a1.65 1.65 0 01-2.25.6l-.88-.51a1.651 1.651 0 00-2.458 1.304l-.116.996a1.65 1.65 0 01-3.35 0l-.116-.996a1.651 1.651 0 00-2.458-1.304l-.88.51a1.65 1.65 0 01-2.25-.6l-1.65-2.86a1.65 1.65 0 01.6-2.25l.88-.51a1.651 1.651 0 000-2.862l-.88-.51a1.65 1.65 0 01-.6-2.25l1.65-2.86a1.65 1.65 0 012.25-.6l.88.51a1.651 1.651 0 002.458-1.304l.116-.996z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 15.5a3.5 3.5 0 100-7 3.5 3.5 0 000 7z"
      stroke={color}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ProfileSelection: React.FC = () => {
  const { handleLogout, accessToken } = useUserData();
  const { setHospitalID, allDoctorsData, setDoctors } = useHospDocData();
  const { loadTVConfiguration } = useTVConfiguration();
  const { queue_v2, isLoading: flagsLoading } = useFeatureFlags();
  const { hospital_id } = useParams();
  const navigate = useNavigate();
  const [selectedProfile, setSelectedProfile] = useState<string>("");
  const [profiles, setProfiles] = useState<ProfileOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingProfile, setLoadingProfile] = useState<string | null>(null);

  useEffect(() => {
    if (hospital_id) {
      setHospitalID(hospital_id);
    }
  }, [hospital_id, setHospitalID]);

  // When the v2 live queue is enabled for this hospital, skip the profile
  // picker entirely and go straight to the v2 config page. queue_v2 defaults
  // to false and only flips true after the flags load, so this never
  // misfires before the real flag value is known.
  useEffect(() => {
    if (queue_v2 && hospital_id) {
      navigate(`/${hospital_id}/v2`, { replace: true });
    }
  }, [queue_v2, hospital_id, navigate]);

  const saveProfileId = (hospitalId: string, profileId: string) => {
    try {
      localStorage.setItem(`selectedProfile_${hospitalId}`, profileId);
    } catch (error) {
      console.error("Error saving profile to localStorage:", error);
    }
  };

  // Helper function to navigate to profile view
  const navigateToProfileView = useCallback(async (profileId: string, profileData?: any) => {
    if (!hospital_id || !accessToken) return;

    try {
      let profileInfo = profileData;
      let isDefault = false;

      // If profileData is not provided, fetch it
      if (!profileInfo) {
        const response = await getProfileByHospitalAndProfileId(hospital_id, profileId);
        
        if (response?.status === 200 && (response?.data?.status === 1 || response?.data?.status === "SUCCESS")) {
          profileInfo = response.data.data || response.data.result || response.data;
          isDefault = profileInfo?.is_default === true;
        } else {
          toast.error("Failed to load profile data");
          return;
        }
      } else {
        isDefault = profileInfo.is_default === true;
      }

      // Save the selected profile ID to localStorage
      if (hospital_id) {
        saveProfileId(hospital_id, profileId);
      }

      // If default profile, navigate to main page
      if (isDefault) {
        navigate(`/${hospital_id}`);
        return;
      }

      if (profileInfo) {
        if (profileInfo.language) {
          setAudioLanguageToLocalStorage(profileInfo.language);
        }

        let selectedDoctors: Doctor[] = [];
        if (profileInfo.doctors_list && Array.isArray(profileInfo.doctors_list) && profileInfo.doctors_list.length > 0) {
          let doctorsToMatch: Doctor[] = [];
          if (!allDoctorsData || allDoctorsData.length === 0) {
            const docResponse = await getDoctorListByHospitalId(hospital_id);
            if (docResponse?.status === 200 && docResponse?.data?.status === "SUCCESS") {
              doctorsToMatch = docResponse.data.result || [];
            }
          } else {
            doctorsToMatch = allDoctorsData;
          }

          if (doctorsToMatch.length > 0) {
            const doctorIds = profileInfo.doctors_list.map((doc: any) => {
              if (typeof doc === 'object' && doc !== null) {
                return doc.id || doc.doctor_id || doc.mapping_id || null;
              }
              return typeof doc === 'string' ? doc.trim() : null;
            }).filter(Boolean);

            selectedDoctors = doctorsToMatch.filter((doc: Doctor) => {
              if (!doc) return false;
              
              return doctorIds.some((profileDoctorId: string) => {
                if (!profileDoctorId) return false;
                
                const normalizedProfileId = String(profileDoctorId).trim().toLowerCase();
                
                if (doc.mapping_id) {
                  const normalizedMappingId = String(doc.mapping_id).trim().toLowerCase();
                  if (normalizedMappingId === normalizedProfileId) {
                    return true;
                  }
                }
                
                if (doc.doctor_id) {
                  const normalizedDoctorId = String(doc.doctor_id).trim().toLowerCase();
                  if (normalizedDoctorId === normalizedProfileId) {
                    return true;
                  }
                }
                
                if ((doc as any).id) {
                  const normalizedId = String((doc as any).id).trim().toLowerCase();
                  if (normalizedId === normalizedProfileId) {
                    return true;
                  }
                }
                
                return false;
              });
            });
          }
        }

        if (hospital_id) {
          await loadTVConfiguration(hospital_id);
        }

        if (selectedDoctors.length === 0 && profileInfo.doctors_list && profileInfo.doctors_list.length > 0) {
          toast.error(`No doctors found for this profile. Found ${profileInfo.doctors_list?.length || 0} doctors in profile but couldn't match them. Please check profile settings.`);
          return;
        }

        if (selectedDoctors.length > 0) {
          setDoctors(selectedDoctors);
          await new Promise(resolve => setTimeout(resolve, 200));
        }

        const viewType = profileInfo.view_type;
        if (viewType === "single-doctor" || viewType === "carousel") {
          navigate(`/${hospital_id}/carousel-view`);
        } else if (viewType === "column") {
          navigate(`/${hospital_id}/column-carousel-view`);
        } else {
          navigate(`/${hospital_id}`);
        }
      } else {
        toast.error("Failed to load profile data");
      }
    } catch (error) {
      console.error("Error loading profile:", error);
      toast.error("Error loading profile. Please try again.");
    }
  }, [hospital_id, accessToken, allDoctorsData, setDoctors, loadTVConfiguration, navigate]);

  useEffect(() => {
    const fetchProfiles = async () => {
      if (!hospital_id || !accessToken) return;

      setLoading(true);
      try {
        const response = await getProfilesByHospitalId(hospital_id);
        
        if (response?.status === 200 && response?.data?.status === "SUCCESS") {
          const apiProfiles = (response.data.result || []).filter((p: any) => p.is_active !== false);

          // Always show this selection page after login — never auto-skip to a
          // single/default profile or the main display. Staff land here so they
          // can pick a view (and reach the "Open Live Queue (v2)" button).
          if (apiProfiles.length > 0) {
            const mappedProfiles: ProfileOption[] = apiProfiles.map((profile: any, index: number) => {
              const profileId = profile.id || profile.tv_config_profile_id || profile.profile_id;
              const profileName = profile.tv_profile_name || profile.profile_name || `Profile ${index + 1}`;
              
              return {
                id: profileId,
                title: profileName,
                icon: "user" as const,
                colors: {
                  defaultBg: index % 2 === 0 ? "#0B74FF" : "#053B7D",
                  activeBg: index % 2 === 0 ? "#0B74FF" : "#053B7D",
                  border: "transparent",
                  activeBorder: index % 2 === 0 ? "#0B74FF" : "#053B7D",
                  icon: "#FFFFFF",
                  activeIcon: "#FFFFFF",
                },
                isDefault: profile.is_default === true,
              };
            });
          
            setProfiles(mappedProfiles);
            setSelectedProfile(mappedProfiles[0].id);
          }
        } else {
          // If API fails, navigate to main page
          setProfiles([]);
          navigate(`/${hospital_id}`);
        }
      } catch (error) {
        console.error("Error fetching profiles:", error);
        setProfiles([]);
        // On error, navigate to main page
        if (hospital_id) {
          navigate(`/${hospital_id}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
    // navigateToProfileView intentionally omitted: it's no longer used here
    // (only by the click handler), and its identity changes every render
    // (depends on allDoctorsData), which would re-fire this fetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospital_id, accessToken, navigate]);

  const handleExistingProfile = async (profileId: string) => {
    if (!hospital_id || !accessToken) return;

    setSelectedProfile(profileId);
    setLoadingProfile(profileId);

    try {
      await navigateToProfileView(profileId);
    } finally {
      setLoadingProfile(null);
    }
  };

  const profileIcon = useMemo(
    () => ({
      user: UserIcon,
      settings: SettingsIcon,
    }),
    []
  );

  return (
    <div className="flex flex-col w-full min-h-screen items-center justify-center px-6 bg-white">
      <button
        className="absolute top-5 right-5 bg-white hover:bg-onLeave hover:text-white flex flex-row items-center space-x-1 text-onLeave border border-onLeave hover:border-red-400 px-5 py-[6px] rounded-[20px]"
        onClick={handleLogout}
      >
        <Logout />
        <p className="text-xs lg:text-base">Logout</p>
      </button>

      <div className="flex flex-col items-center w-full max-w-4xl text-center bg-white/80 rounded-[32px] px-6 py-12 shadow-xl">
        <Logo />
        <h1 className="text-[28px] md:text-[36px] font-semibold text-[#1F2B4D] mt-10">
          Select Your View
        </h1>
        <p className="text-sm md:text-base text-gray-500 mt-2">
          Choose a profile to continue to DocTrue TV.
        </p>

        {loading || flagsLoading || queue_v2 ? (
          <div className="mt-16 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B74FF]"></div>
          </div>
        ) : profiles.length === 0 ? (
          <div className="mt-16 flex flex-col items-center justify-center">
            <p className="text-gray-500 text-lg">No profiles available</p>
            <p className="text-gray-400 text-sm mt-2">Please create a profile in settings</p>
          </div>
        ) : (
          <div className="mt-16 flex flex-wrap items-center justify-center gap-6 md:gap-8">
            {profiles.map((profile) => (
            <button
              key={profile.id}
              className="flex flex-col items-center focus:outline-none relative"
              onClick={() => handleExistingProfile(profile.id)}
              disabled={loadingProfile === profile.id}
            >
              {loadingProfile === profile.id && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 rounded-[18px] z-10">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                </div>
              )}
              <div
                className={`w-[120px] h-[120px] md:w-[140px] md:h-[140px] rounded-[18px] shadow-sm transition-all duration-200 flex items-center justify-center border-2 ${
                  selectedProfile === profile.id ? "shadow-lg" : ""
                } ${loadingProfile === profile.id ? "opacity-75" : ""}`}
                style={{
                  backgroundColor: selectedProfile === profile.id ? profile.colors.activeBg : profile.colors.defaultBg,
                  borderColor: selectedProfile === profile.id ? "#2668FF" : profile.colors.border ?? "transparent",
                }}
              >
                {React.createElement(profileIcon[profile.icon], {
                  color: selectedProfile === profile.id ? profile.colors.activeIcon ?? profile.colors.icon : profile.colors.icon,
                })}
              </div>
              <p className="mt-4 text-sm md:text-base font-medium text-[#1F2B4D] text-center">
                {profile.title}
              </p>
            </button>
          ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default ProfileSelection;

